import { Button } from "@/components/ui/button";
import { DocumentStatusBadge } from "./DocumentStatusBadge";
import { Edit2, Eye, FileIcon, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";

interface DocumentRowProps {
  doc: any;
  canEditFile: (doc: any) => boolean;
  onViewFile: (bucket: "profiles" | "applications", filePath: string) => void;
  onEditDocument: (doc: any) => void;
  onDeleteDocument: (documentId: number) => void;
}

export function DocumentRow({
  doc,
  canEditFile,
  onViewFile,
  onEditDocument,
  onDeleteDocument
}: DocumentRowProps) {
  const { t } = useTranslation();
  const { data: documentTypes = [] } = useDocumentTypes();

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 py-3 px-3 md:px-4 border rounded-md items-center hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-2 md:col-span-4">
        <FileIcon className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="truncate font-medium md:font-normal">
          {doc.file_name}
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm text-muted-foreground md:col-span-3 md:text-foreground md:text-base">
        <span className="truncate">
          {documentTypes.find((t) => t.id === doc.document_type_id)?.name ||
            "Unbekannt"}
        </span>
      </div>
      <div className="flex items-center md:col-span-2">
        <DocumentStatusBadge status={doc.document_status} />
      </div>
      <div className="flex items-center gap-2 md:col-span-3 md:justify-end mt-2 md:mt-0">
        <Button
          variant="ghost"
          size="icon"
          title={t("common:actions.view", "Ansehen")}
          onClick={() =>
            onViewFile(
              doc.application_id ? "applications" : "profiles",
              doc.file_path
            )
          }
        >
          <Eye className="h-4 w-4" />
        </Button>
        {canEditFile(doc) && (
          <Button
            variant="ghost"
            size="icon"
            title={t("common:actions.edit", "Bearbeiten")}
            onClick={() => onEditDocument(doc)}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
        )}
        {canEditFile(doc) && (
          <Button
            id="delete-document-button"
            variant="ghost"
            size="icon"
            title={t("common:actions.delete", "Löschen")}
            onClick={() => onDeleteDocument(doc.id)}
          >
            <Trash2 className="h-4 w-4 text-red-500 md:text-inherit hover:text-red-600" />
          </Button>
        )}
      </div>
    </div>
  );
}
